import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FacebookLogo, InstagramLogo, LinkedinLogo, TiktokLogo, YoutubeLogo,
  PlugsConnected, Plugs, CheckCircle, WarningCircle, UploadSimple, X, PaperPlaneTilt, ShareNetwork, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";

const PLATFORM_META = {
  facebook: { label: "Facebook", Icon: FacebookLogo, color: "#1877F2" },
  instagram: { label: "Instagram", Icon: InstagramLogo, color: "#E1306C" },
  linkedin: { label: "LinkedIn", Icon: LinkedinLogo, color: "#0A66C2" },
  tiktok: { label: "TikTok", Icon: TiktokLogo, color: "#010101" },
  youtube: { label: "YouTube", Icon: YoutubeLogo, color: "#FF0000" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const SocialConnections = () => {
  const api = useApi();
  const [params, setParams] = useSearchParams();

  const [metaStatus, setMetaStatus] = useState({});
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaBusy, setMetaBusy] = useState(false);

  const [fbDirectStatus, setFbDirectStatus] = useState({});
  const [refreshTokenOpen, setRefreshTokenOpen] = useState(false);
  const [refreshTokenValue, setRefreshTokenValue] = useState("");
  const [refreshBusy, setRefreshBusy] = useState(false);

  const [nativeStatus, setNativeStatus] = useState({});
  const [nativeLoading, setNativeLoading] = useState(true);
  const [nativeBusy, setNativeBusy] = useState(null);

  const [historyItems, setHistoryItems] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Quick Post
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState(null); // {media_id, file_url, kind, filename}
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  const [mediaOptions, setMediaOptions] = useState([]);
  const [librarySelectKey, setLibrarySelectKey] = useState(0);

  const loadMediaOptions = useCallback(async () => {
    try {
      const r = await api.get("/media");
      setMediaOptions(r.data || []);
    } catch (e) { /* leave previous state */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMeta = useCallback(async () => {
    setMetaLoading(true);
    try {
      const r = await api.get("/social/meta-oauth/status");
      setMetaStatus(r.data || {});
    } catch (e) { /* leave previous state */ }
    finally { setMetaLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFbDirect = useCallback(async () => {
    try {
      const r = await api.get("/facebook-direct/status");
      setFbDirectStatus(r.data || {});
    } catch (e) { /* leave previous state — legacy fallback, not critical if unreachable */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNative = useCallback(async () => {
    setNativeLoading(true);
    try {
      const r = await api.get("/social/native/status");
      setNativeStatus(r.data || {});
    } catch (e) { /* leave previous state */ }
    finally { setNativeLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/facebook-direct/history?limit=20");
      setHistoryItems(r.data?.items || []);
    } catch (e) { /* leave previous state */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadMeta(); loadFbDirect(); loadNative(); loadHistory(); loadMediaOptions(); }, [loadMeta, loadFbDirect, loadNative, loadHistory, loadMediaOptions]);

  useEffect(() => {
    const flag = params.get("meta_oauth");
    const nativeFlag = params.get("social_native");
    const result = params.get("status");
    const reason = params.get("reason");
    if (flag && result === "connected") { toast.success("Facebook & Instagram connected"); loadMeta(); }
    else if (flag && result === "error") { toast.error(reason || "Couldn't connect Facebook — please try again", { duration: 10000 }); }
    if (nativeFlag && result === "connected") { toast.success(`${nativeFlag} connected`); loadNative(); }
    else if (nativeFlag && result === "error") { toast.error(`Couldn't connect ${nativeFlag}. Please try again.`); }
    if (flag || nativeFlag) {
      params.delete("meta_oauth"); params.delete("social_native"); params.delete("status"); params.delete("reason");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const metaPages = metaStatus.pages || [];
  const needsPageSelection = metaStatus.connected && metaPages.length > 1 && !metaStatus.ready;
  const selectedPage = metaPages.find((p) => p.id === metaStatus.selected_page_id);
  const instagramLinked = !!(metaStatus.ready && selectedPage?.instagram_id);
  // Facebook/Instagram may be connected via either the new OAuth popup OR the legacy manual-token
  // connector (hidden from this UI per design, but kept working as a silent fallback) — a workspace
  // on the legacy connection must still show as Connected here and be usable in Quick Post.
  const facebookConnected = !!metaStatus.ready || !!fbDirectStatus.connected;
  const instagramConnected = instagramLinked || !!(fbDirectStatus.connected && fbDirectStatus.instagram_linked);
  const facebookViaLegacy = !metaStatus.ready && !!fbDirectStatus.connected;

  const connectMeta = async () => {
    setMetaBusy(true);
    try {
      const r = await api.get("/social/meta-oauth/connect");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Facebook login — please try again");
      setMetaBusy(false);
    }
  };

  const disconnectMeta = async () => {
    setMetaBusy(true);
    try {
      await api.post("/social/meta-oauth/disconnect");
      toast.success("Disconnected");
      await loadMeta();
    } catch (e) { toast.error("Couldn't disconnect"); }
    finally { setMetaBusy(false); }
  };

  const refreshFacebookPermissions = async () => {
    if (!refreshTokenValue.trim()) { toast.error("Paste a new access token first"); return; }
    setRefreshBusy(true);
    try {
      const r = await api.post("/facebook-direct/list-pages", { user_access_token: refreshTokenValue.trim() });
      const page = (r.data?.pages || []).find((p) => String(p.id) === String(fbDirectStatus.page_id));
      if (!page) { toast.error("That token doesn't manage the connected Page — use the same Facebook account/Page."); return; }
      await api.put("/facebook-direct/connect", { page_id: page.id, page_access_token: page.access_token });
      toast.success("Permissions refreshed");
      setRefreshTokenOpen(false); setRefreshTokenValue("");
      await loadFbDirect();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't refresh — check the token and try again");
    } finally { setRefreshBusy(false); }
  };

  const selectMetaPage = async (pageId) => {
    setMetaBusy(true);
    try {
      await api.post("/social/meta-oauth/select", { page_id: pageId });
      toast.success("Page selected");
      await loadMeta();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't select that Page"); }
    finally { setMetaBusy(false); }
  };

  const connectNative = async (provider) => {
    setNativeBusy(provider);
    try {
      const r = await api.get(`/social/${provider}/connect`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${provider} isn't configured on the server yet.`);
      setNativeBusy(null);
    }
  };

  const disconnectNative = async (provider) => {
    setNativeBusy(provider);
    try {
      await api.post(`/social/${provider}/disconnect`);
      toast.success("Disconnected");
      await loadNative();
    } catch (e) { toast.error("Couldn't disconnect"); }
    finally { setNativeBusy(null); }
  };

  const selectNativeAccount = async (provider, accountId) => {
    setNativeBusy(provider);
    try {
      await api.post(`/social/${provider}/select`, { account_id: accountId });
      toast.success("Account selected");
      await loadNative();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't select that account"); }
    finally { setNativeBusy(null); }
  };

  const isConnected = (platform) => {
    if (platform === "facebook") return facebookConnected;
    if (platform === "instagram") return instagramConnected;
    return !!nativeStatus[platform]?.ready;
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms((prev) => prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      const r = await api.post("/media/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const uploaded = r.data?.media?.[0];
      if (!uploaded) throw new Error("Upload returned no file");
      setMedia({ media_id: uploaded.id, file_url: uploaded.file_url, kind: uploaded.kind, filename: uploaded.filename });
      setMediaOptions((prev) => [uploaded, ...prev]);
      toast.success(`Uploaded ${uploaded.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const pickFromLibrary = (mediaId) => {
    const m = mediaOptions.find((x) => x.id === mediaId);
    if (!m) return;
    setMedia({ media_id: m.id, file_url: m.file_url, kind: m.kind, filename: m.filename });
    setLibrarySelectKey((k) => k + 1); // resets the trigger's displayed label so it never looks "stuck" on the last pick
  };

  const clearMedia = () => setMedia(null);

  const sendQuickPost = async () => {
    if (selectedPlatforms.length === 0) { toast.error("Pick at least one platform"); return; }
    if (!caption.trim() && !media) { toast.error("Write a caption or attach a file first"); return; }
    setPosting(true);
    try {
      const r = await api.post("/social/quick-post", { platforms: selectedPlatforms, text: caption, media_id: media?.media_id || null });
      const mode = r.data?.mode;
      if (mode === "published") toast.success("Published live");
      else if (mode === "drafted") toast.success("Saved to the Approval Queue for review", { description: r.data?.detail });
      else toast.success(r.data?.detail || "Saved");
      setCaption(""); setMedia(null);
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that post");
    } finally { setPosting(false); }
  };

  const connectablePlatforms = Object.keys(PLATFORM_META);

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-5" data-testid="social-connections-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-[10px] bg-[#f0f7f8] flex items-center justify-center text-[#3A7A8C] shrink-0">
            <ShareNetwork size={19} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat" }}>Social Connections</h2>
            <p className="text-[#7A9090] text-sm">Connect once, then publish straight from your Approval Queue or Quick Post below.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {connectablePlatforms.map((platform) => {
            const { label, Icon, color } = PLATFORM_META[platform];
            const isMeta = platform === "facebook" || platform === "instagram";
            const connected = isConnected(platform);
            const busy = isMeta ? metaBusy : nativeBusy === platform;
            const configured = isMeta ? true : !!nativeStatus[platform]?.configured;
            const loading = isMeta ? metaLoading : nativeLoading;
            return (
              <div key={platform} className="border border-[#e5e7eb] rounded-[10px] p-4 space-y-2" data-testid={`platform-card-${platform}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Icon size={20} weight="fill" style={{ color }} />
                  <span className="font-semibold text-[#3C4048] text-sm">{label}</span>
                  {loading ? null : connected ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#dcfce7] text-[#15803d] text-xs font-semibold ml-auto whitespace-nowrap shrink-0" data-testid={`status-connected-${platform}`}>
                      <CheckCircle size={11} weight="fill" /> Connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f5f5f5] text-[#7A9090] text-xs font-medium ml-auto whitespace-nowrap shrink-0" data-testid={`status-not-connected-${platform}`}>Not connected</span>
                  )}
                </div>
                {platform === "facebook" && metaStatus.connected && selectedPage && (
                  <p className="text-[#7A9090] text-xs truncate">{selectedPage.name}</p>
                )}
                {platform === "facebook" && facebookViaLegacy && (
                  <p className="text-[#7A9090] text-xs truncate">{fbDirectStatus.page_name || "Connected"}</p>
                )}
                {platform === "instagram" && metaStatus.ready && (
                  <p className="text-[#7A9090] text-xs truncate">
                    {instagramLinked ? `@${selectedPage?.instagram_username || selectedPage?.instagram_id}` : "No Instagram linked to this Page yet"}
                  </p>
                )}
                {platform === "instagram" && !metaStatus.ready && instagramConnected && (
                  <p className="text-[#7A9090] text-xs truncate">@{fbDirectStatus.ig_username}</p>
                )}
                {platform === "instagram" && !instagramConnected && (
                  <p className="text-[10px] text-[#7A9090]">Comes automatically once linked to your Facebook Page — no separate connect step.</p>
                )}
                {platform !== "facebook" && platform !== "instagram" && nativeStatus[platform]?.channel_title && (
                  <p className="text-[#7A9090] text-xs truncate">{nativeStatus[platform].channel_title}</p>
                )}
                <div className="flex items-center gap-2">
                  {isMeta ? (
                    platform === "facebook" ? (
                      facebookViaLegacy ? (
                        <div className="space-y-1.5">
                          <p className="text-[10px] text-[#7A9090]">Connected via an existing setup — ready to use below.</p>
                          <button data-testid="refresh-fb-permissions-toggle" onClick={() => setRefreshTokenOpen((v) => !v)}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#3A7A8C] hover:underline p-2 -m-2">
                            <ArrowsClockwise size={11} weight="bold" /> Refresh permissions
                          </button>
                          {refreshTokenOpen && (
                            <div className="space-y-1.5 pt-1">
                              <p className="text-[10px] text-[#7A9090]">
                                Generate a new token at <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="underline font-medium text-[#3A7A8C]">Graph API Explorer</a> with
                                {" "}<code className="bg-[#f5f5f5] rounded px-1">pages_show_list</code>, <code className="bg-[#f5f5f5] rounded px-1">pages_read_engagement</code>, <code className="bg-[#f5f5f5] rounded px-1">pages_manage_posts</code>, <code className="bg-[#f5f5f5] rounded px-1">instagram_basic</code>, <code className="bg-[#f5f5f5] rounded px-1">instagram_content_publish</code> and <code className="bg-[#f5f5f5] rounded px-1">instagram_manage_insights</code> (the last one unlocks Audience Demographics in Analytics).
                              </p>
                              <Textarea data-testid="refresh-fb-token-input" placeholder="Paste a new Graph API Explorer user access token…"
                                value={refreshTokenValue} onChange={(e) => setRefreshTokenValue(e.target.value)}
                                className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] text-xs min-h-[50px]" />
                              <Button data-testid="refresh-fb-permissions-submit" size="sm" disabled={refreshBusy} onClick={refreshFacebookPermissions}
                                className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[8px] text-xs h-7">
                                {refreshBusy ? "Refreshing…" : "Refresh"}
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : !metaStatus.connected ? (
                        <Button data-testid="connect-btn-facebook" size="sm" onClick={connectMeta} disabled={busy || !metaStatus.configured} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[8px] gap-1.5 text-xs h-8">
                          <PlugsConnected size={13} weight="fill" /> {busy ? "Redirecting…" : "Connect"}
                        </Button>
                      ) : (
                        <Button data-testid="disconnect-btn-facebook" size="sm" onClick={disconnectMeta} disabled={busy} variant="outline" className="border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white rounded-[8px] gap-1.5 text-xs h-8">
                          <Plugs size={13} /> Disconnect
                        </Button>
                      )
                    ) : (
                      <p className="text-[10px] text-[#7A9090]" data-testid="instagram-auto-note">
                        {instagramConnected ? "Managed together with Facebook above." : "Connect Facebook above, then link Instagram to that Page in Meta Business Suite."}
                      </p>
                    )
                  ) : (
                    !nativeStatus[platform]?.connected ? (
                      <Button data-testid={`connect-btn-${platform}`} size="sm" onClick={() => connectNative(platform)} disabled={!configured || busy} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[8px] gap-1.5 text-xs h-8">
                        <PlugsConnected size={13} weight="fill" /> {busy ? "Redirecting…" : "Connect"}
                      </Button>
                    ) : (
                      <>
                        <Button data-testid={`reconnect-btn-${platform}`} size="sm" onClick={() => connectNative(platform)} disabled={busy} variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#5FA8B4] hover:text-white rounded-[8px] gap-1.5 text-xs h-8">
                          <ArrowsClockwise size={13} /> {busy ? "Redirecting…" : "Reconnect"}
                        </Button>
                        <Button data-testid={`disconnect-btn-${platform}`} size="sm" onClick={() => disconnectNative(platform)} disabled={busy} variant="outline" className="border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white rounded-[8px] gap-1.5 text-xs h-8">
                          <Plugs size={13} /> Disconnect
                        </Button>
                      </>
                    )
                  )}
                </div>
                {platform === "facebook" && needsPageSelection && (
                  <Select onValueChange={selectMetaPage} disabled={metaBusy}>
                    <SelectTrigger data-testid="select-page-facebook" className="bg-[#fafafa] border-[#e5e7eb] text-xs h-8">
                      <SelectValue placeholder="Choose a Page…" />
                    </SelectTrigger>
                    <SelectContent>
                      {metaPages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {platform === "linkedin" && nativeStatus.linkedin?.connected && (nativeStatus.linkedin.organizations || []).length > 1 && !nativeStatus.linkedin.ready && (
                  <Select onValueChange={(v) => selectNativeAccount("linkedin", v)} disabled={nativeBusy === "linkedin"}>
                    <SelectTrigger data-testid="select-account-linkedin" className="bg-[#fafafa] border-[#e5e7eb] text-xs h-8">
                      <SelectValue placeholder="Choose a Company Page…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(nativeStatus.linkedin.organizations || []).map((o) => <SelectItem key={o.id || o.urn} value={o.id || o.urn}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {!configured && !isMeta && (
                  <p className="text-[10px] text-[#a16207]">Not set up on the server yet.</p>
                )}
              </div>
            );
          })}
        </div>

        {metaStatus.connected && (
          <p className="text-[#a16207] text-xs bg-[#fef7e0] border border-[#f0d98a] rounded-[8px] px-2 py-1.5">
            Meta&rsquo;s &quot;Standard Access&quot; tier only reliably connects Pages related to our Meta app —
            connecting an unrelated company&rsquo;s Page may fail until Advanced Access (Business Verification + App Review) is approved.
          </p>
        )}
      </div>

      <div className="bg-white border border-[#5FA8B4] rounded-[12px] p-6 space-y-3" data-testid="quick-post-card">
        <div>
          <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat" }}>Quick Post</h2>
          <p className="text-[#7A9090] text-sm">Publish straight to any connected account right now — images publish instantly everywhere; video only instant-publishes on Facebook/Instagram today (LinkedIn/TikTok/YouTube video is saved to the Approval Queue for review instead).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="quick-post-platform-picker">
          {connectablePlatforms.filter((p) => isConnected(p)).map((platform) => {
            const { label, Icon, color } = PLATFORM_META[platform];
            const active = selectedPlatforms.includes(platform);
            return (
              <button key={platform} type="button" onClick={() => togglePlatform(platform)} data-testid={`quick-post-toggle-${platform}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${active ? "border-[#5FA8B4] bg-[#f0f7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#7A9090] hover:border-[#5FA8B4]"}`}>
                <Icon size={14} weight="fill" style={{ color: active ? color : undefined }} /> {label}
              </button>
            );
          })}
          {connectablePlatforms.every((p) => !isConnected(p)) && (
            <p className="text-[#7A9090] text-xs" data-testid="quick-post-no-accounts">Connect an account above first.</p>
          )}
        </div>
        <Textarea
          data-testid="quick-post-text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Write your caption…"
          className="rounded-[10px] bg-[#fafafa] border-[#e5e7eb]"
          rows={3}
        />
        <div className="space-y-1.5">
          {media && (
            <div className="flex items-center gap-2 p-2 bg-[#fafafa] border border-[#e5e7eb] rounded-[10px]" data-testid="quick-post-media-preview">
              {media.kind === "image" ? (
                <img src={media.file_url} alt="Selected" className="w-10 h-10 rounded-[6px] object-cover shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-[6px] bg-[#e5e7eb] flex items-center justify-center text-[10px] uppercase text-[#7A9090] shrink-0">{media.kind}</div>
              )}
              <span className="text-xs text-[#3C4048] truncate flex-1">{media.filename}</span>
              <button type="button" onClick={clearMedia} data-testid="quick-post-clear-media-btn" className="text-[#7A9090] hover:text-[#ef4444] transition-colors shrink-0" title="Remove — clears the attachment, doesn't post it">
                <X size={16} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button asChild size="sm" variant="outline" disabled={uploading} className="h-9 border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 cursor-pointer">
              <label data-testid="quick-post-upload-btn" htmlFor="quick-post-file-input">
                <UploadSimple size={15} /> {uploading ? "Uploading…" : "Upload from computer"}
              </label>
            </Button>
            <input id="quick-post-file-input" type="file" accept="image/*,video/*,audio/*" className="hidden" onChange={uploadFile} disabled={uploading} data-testid="quick-post-file-input" />
            <Select key={librarySelectKey} onValueChange={pickFromLibrary}>
              <SelectTrigger data-testid="quick-post-library-select" className="w-[200px] h-9 bg-[#fafafa] border-[#e5e7eb] text-xs">
                <SelectValue placeholder="Choose from Library…" />
              </SelectTrigger>
              <SelectContent>
                {mediaOptions.length === 0 && (
                  <div className="px-3 py-2 text-xs text-[#7A9090]">No media in your Library yet</div>
                )}
                {mediaOptions.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`quick-post-library-item-${m.id}`}>
                    <div className="flex items-center gap-2">
                      {m.kind === "image" ? (
                        <img src={m.file_url} alt="" loading="lazy" className="w-6 h-6 rounded object-cover shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded bg-[#e5e7eb] flex items-center justify-center text-[8px] uppercase text-[#7A9090] shrink-0">{m.kind?.[0]}</div>
                      )}
                      <span className="truncate">{m.filename}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-[11px] text-[#5B6B6B]">Image, video or audio</span>
          </div>
        </div>
        <Button
          data-testid="quick-post-send-btn"
          onClick={sendQuickPost}
          disabled={posting}
          className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[10px] gap-1.5"
        >
          <PaperPlaneTilt size={15} weight="fill" /> {posting ? "Sending…" : "Post Now"}
        </Button>
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-3" data-testid="publish-history-card">
        <button type="button" onClick={() => setHistoryOpen((v) => !v)} className="w-full flex items-center justify-between gap-3" data-testid="publish-history-toggle">
          <div>
            <h2 className="text-lg font-bold text-[#3C4048]" style={{ fontFamily: "Montserrat" }}>Recent Activity</h2>
            <p className="text-[#7A9090] text-sm">What&rsquo;s actually gone out across your connected accounts.</p>
          </div>
          <span className="text-[#7A9090] text-sm">{historyOpen ? "Hide" : "Show"}</span>
        </button>
        {historyOpen && (
          historyItems.length === 0 ? (
            <p className="text-[#7A9090] text-sm" data-testid="publish-history-empty">Nothing published yet.</p>
          ) : (
            <div className="divide-y divide-[#e5e7eb]" data-testid="publish-history-list">
              {historyItems.map((item) => (
                <div key={item.id} className="py-3 flex items-start justify-between gap-3" data-testid={`publish-history-item-${item.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase text-[#3A7A8C]">{item.platform}</span>
                      <span className="text-[#7A9090] text-xs">{timeAgo(item.published_at)}</span>
                    </div>
                    <p className="text-[#3C4048] text-sm mt-0.5 truncate">{item.message || "(no caption)"}</p>
                  </div>
                  {item.permalink && (
                    <a href={item.permalink} target="_blank" rel="noreferrer" className="text-[#3A7A8C] text-sm font-medium underline shrink-0">View post ↗</a>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <p className="text-[#7A9090] text-xs">
        Want deeper insight into link clicks from your posts? Check the <Link to="/analytics" className="text-[#3A7A8C] font-semibold hover:underline" data-testid="link-analytics">Analytics dashboard</Link>.
      </p>
    </div>
  );
};
